import React, { useCallback, useEffect, useMemo, useState } from "react";
import classNames from "classnames";
import * as XLSX from "xlsx";
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Form,
  FormGroup,
  Input,
  Label,
  Nav,
  NavItem,
  NavLink,
  Row,
  Spinner,
  TabContent,
  TabPane,
  Table,
} from "reactstrap";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import { requestJson } from "../../../helpers/api";

// ─── Types ────────────────────────────────────────────────────────────────────

type ControlCaja = {
  idControlCaja: number;
  idCaja: number | null;
  caja: string | null;
  sucursal: string | null;
  fechaApertura: string | null;
  fechaCierre: string | null;
  estado: string | null;
  cajero: string | null;
};

type PlanillaRegistro = {
  // campos del servidor
  seccion: string;
  pexpedicion: number | null;
  nroRecibo: number | null;
  idTipoRecibo: number | null;
  fechaRecibo: string | null;
  idMoneda: string | null;
  idCuenta: number | null;
  estado: string | null;
  estadoFactura: string | null;
  idMovimiento: number | null;
  montoVenta: number;
  montoPedido: number;
  montoOrig: number;
  montoUsdOrig: number;
  nroMovimiento: number | null;
  fechaFactura: string | null;
  idPersona: number | null;
  persona: string | null;
  idTipoMovimiento: number | null;
  tipoMovimiento: string | null;
  idRecibo: number | null;
  ingresoMonto: number;
  ingresoMontoUsd: number;
  egresoMonto: number;
  egresoMontoUsd: number;
  estadoCheck: string | null;
  idSucursal: number | null;
  sucursal: string | null;
  tipo: number | null;
  tipoRecibo: string | null;
  idPlanCuenta: string | null;
  planCuenta: string | null;
  concepto: string | null;
  modulo: number | null;
  idMon: number;
  formato: string | null;
  nroPedido: number | null;
  importePedido: number;
  canalVenta: string | null;
  fechaCarga: string | null;
  // campo agregado al mergear múltiples controles
  idControlCaja?: number;
};

type PlanillaResponse = {
  encabezado: {
    idControlCaja: number;
    idCaja: number | null;
    caja: string | null;
    cajero: string | null;
    encargado: string | null;
    fechaApertura: string | null;
    fechaCierre: string | null;
    estadoCaja: string | null;
    fuente: string;
  };
  registros: PlanillaRegistro[];
  resumen: { totalIngresoGs: number; totalEgresoGs: number; diferencia: number };
};

// ─── Constants ───────────────────────────────────────────────────────────────

const TABS = [
  { id: "planilla",  label: "Planilla",      icon: "ri-file-list-3-line"  },
  { id: "sucursal",  label: "Por Sucursal",  icon: "ri-building-line"     },
  { id: "canal",     label: "Por Canal",     icon: "ri-route-line"        },
  { id: "hora",      label: "Por Hora",      icon: "ri-time-line"         },
  { id: "seccion",   label: "Por Sección",   icon: "ri-layout-grid-line"  },
  { id: "anulados",  label: "Anulados",      icon: "ri-close-circle-line" },
  { id: "clientes",  label: "Por Clientes",  icon: "ri-user-line"         },
  { id: "libro",     label: "Libro Ventas",  icon: "ri-book-open-line"    },
] as const;

type TabId = (typeof TABS)[number]["id"];

// ─── Helpers ─────────────────────────────────────────────────────────────────

const hoy = () => new Date().toISOString().slice(0, 10);
const fmt = new Intl.NumberFormat("es-PY", { maximumFractionDigits: 0 });

const fmtFecha = (iso: string | null | undefined): string => {
  if (!iso) return "-";
  try {
    return new Date(iso).toLocaleString("es-PY", {
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
};

const fmtSoloFecha = (iso: string | null | undefined): string => {
  if (!iso) return "-";
  try {
    return new Date(iso).toLocaleDateString("es-PY", {
      day: "2-digit", month: "2-digit", year: "numeric",
    });
  } catch { return iso; }
};

const fmtSoloHora = (iso: string | null | undefined): string => {
  if (!iso) return "-";
  try {
    return new Date(iso).toLocaleTimeString("es-PY", { hour: "2-digit", minute: "2-digit" });
  } catch { return "-"; }
};

const seccionLabel: Record<string, string> = {
  venta_contado:  "Venta Contado",
  recibo_modulo1: "Recibo/Pago",
  venta_credito:  "Venta Crédito",
};

const seccionColor: Record<string, string> = {
  venta_contado:  "success",
  recibo_modulo1: "info",
  venta_credito:  "warning",
};

const unique = (vals: (string | null | undefined)[]): string[] =>
  Array.from(new Set(vals.filter((v): v is string => !!v))).sort();

// ─── Aggregation helper ───────────────────────────────────────────────────────

type GrupoRow = { grupo: string; ingreso: number; egreso: number; count: number };

const agrupar = (
  items: PlanillaRegistro[],
  key: (r: PlanillaRegistro) => string,
): GrupoRow[] => {
  const map = new Map<string, GrupoRow>();
  for (const r of items) {
    const k = key(r) || "(Sin datos)";
    const v = map.get(k) ?? { grupo: k, ingreso: 0, egreso: 0, count: 0 };
    v.ingreso += r.ingresoMonto;
    v.egreso += r.egresoMonto;
    v.count++;
    map.set(k, v);
  }
  return Array.from(map.values()).sort((a, b) => b.ingreso - a.ingreso);
};

// ─── SummaryTable ─────────────────────────────────────────────────────────────

const SummaryTable = ({ rows, grupoLabel }: { rows: GrupoRow[]; grupoLabel: string }) => {
  const totalIngreso = rows.reduce((a, r) => a + r.ingreso, 0);
  const totalEgreso  = rows.reduce((a, r) => a + r.egreso, 0);
  const totalCount   = rows.reduce((a, r) => a + r.count, 0);
  const neto         = totalIngreso - totalEgreso;

  if (rows.length === 0)
    return <div className="text-center text-muted py-4">Sin datos para mostrar.</div>;

  return (
    <div className="table-responsive">
      <Table className="table-sm table-hover mb-0" striped>
        <thead className="table-light">
          <tr>
            <th>{grupoLabel}</th>
            <th className="text-center">Registros</th>
            <th className="text-end">Ingreso Gs.</th>
            <th className="text-end">Egreso Gs.</th>
            <th className="text-end">Neto Gs.</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i}>
              <td>{r.grupo}</td>
              <td className="text-center">{r.count}</td>
              <td className="text-end text-success fw-semibold">
                {r.ingreso > 0 ? fmt.format(r.ingreso) : ""}
              </td>
              <td className="text-end text-danger fw-semibold">
                {r.egreso > 0 ? fmt.format(r.egreso) : ""}
              </td>
              <td className={classNames("text-end fw-bold", r.ingreso - r.egreso >= 0 ? "text-success" : "text-danger")}>
                {fmt.format(r.ingreso - r.egreso)}
              </td>
            </tr>
          ))}
        </tbody>
        <tfoot className="table-light fw-bold">
          <tr>
            <td>Total</td>
            <td className="text-center">{totalCount}</td>
            <td className="text-end text-success">{fmt.format(totalIngreso)}</td>
            <td className="text-end text-danger">{fmt.format(totalEgreso)}</td>
            <td className={classNames("text-end", neto >= 0 ? "text-success" : "text-danger")}>
              {fmt.format(neto)}
            </td>
          </tr>
        </tfoot>
      </Table>
    </div>
  );
};

// ─── TablaMovimientos (fuera del componente para evitar remounts) ─────────────

const TablaMovimientos = ({ registros }: { registros: PlanillaRegistro[] }) => (
  <div className="table-responsive">
    <Table className="table-sm table-hover mb-0" striped>
      <thead className="table-light">
        <tr>
          <th>Sección</th>
          <th>Nro.</th>
          <th>Fecha</th>
          <th>Persona / Cuenta</th>
          <th>Tipo movimiento</th>
          <th>Tipo recibo</th>
          <th>Canal venta</th>
          <th>Sucursal</th>
          <th className="text-end">Ingreso Gs.</th>
          <th className="text-end">Egreso Gs.</th>
          <th className="text-center">Estado</th>
        </tr>
      </thead>
      <tbody>
        {registros.length === 0 ? (
          <tr>
            <td colSpan={11} className="text-center text-muted py-4">
              Sin movimientos para los filtros seleccionados.
            </td>
          </tr>
        ) : (
          registros.map((r, i) => (
            <tr key={i}>
              <td>
                <Badge color={seccionColor[r.seccion] ?? "secondary"} style={{ fontSize: "0.65rem" }}>
                  {seccionLabel[r.seccion] ?? r.seccion}
                </Badge>
              </td>
              <td className="text-nowrap">{r.nroMovimiento ?? r.nroRecibo ?? "-"}</td>
              <td className="text-nowrap">{fmtFecha(r.fechaFactura ?? r.fechaRecibo)}</td>
              <td>{r.persona ?? r.planCuenta ?? "-"}</td>
              <td>{r.tipoMovimiento ?? "-"}</td>
              <td>{r.tipoRecibo ?? "-"}</td>
              <td>{r.canalVenta ?? "-"}</td>
              <td>{r.sucursal ?? "-"}</td>
              <td className="text-end text-success fw-semibold">
                {r.ingresoMonto > 0 ? fmt.format(r.ingresoMonto) : ""}
              </td>
              <td className="text-end text-danger fw-semibold">
                {r.egresoMonto > 0 ? fmt.format(r.egresoMonto) : ""}
              </td>
              <td className="text-center">
                {r.estadoCheck === "w" ? (
                  <Badge color="danger" pill>Anulado</Badge>
                ) : r.estadoCheck === "v" ? (
                  <Badge color="success" pill>OK</Badge>
                ) : (
                  <span className="text-muted">-</span>
                )}
              </td>
            </tr>
          ))
        )}
      </tbody>
      {registros.length > 0 && (
        <tfoot className="table-light fw-bold">
          <tr>
            <td colSpan={8} className="text-end pe-3">
              Totales ({registros.length} registros):
            </td>
            <td className="text-end text-success">
              {fmt.format(registros.reduce((a, r) => a + r.ingresoMonto, 0))}
            </td>
            <td className="text-end text-danger">
              {fmt.format(registros.reduce((a, r) => a + r.egresoMonto, 0))}
            </td>
            <td />
          </tr>
        </tfoot>
      )}
    </Table>
  </div>
);

// ─── Page ─────────────────────────────────────────────────────────────────────

const VentasPlanillaCajaPage = () => {
  // Parámetros de búsqueda
  const [fuentes, setFuentes] = useState<string[]>([]);
  const [fuente, setFuente] = useState("");
  const [fechaDesde, setFechaDesde] = useState(hoy());
  const [fechaHasta, setFechaHasta] = useState(hoy());

  // Controles cargados (para el select filtro)
  const [controles, setControles] = useState<ControlCaja[]>([]);

  // Todos los registros mergeados
  const [todosRegistros, setTodosRegistros] = useState<PlanillaRegistro[]>([]);
  const [fuenteCargada, setFuenteCargada] = useState("");
  const [rangoLabel, setRangoLabel] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Tabs
  const [activeTab, setActiveTab] = useState<TabId>("planilla");

  // Filtros de planilla
  const [filtroControl, setFiltroControl] = useState("");
  const [filtroPersona, setFiltroPersona] = useState("");
  const [filtroTipoMov, setFiltroTipoMov] = useState("");
  const [filtroTipoRecibo, setFiltroTipoRecibo] = useState("");
  const [filtroCajero, setFiltroCajero] = useState("");
  const [filtroCanal, setFiltroCanal] = useState("");
  const [filtroSucursal, setFiltroSucursal] = useState("");

  useEffect(() => {
    document.title = "Ventas - Planilla Caja | Saas";
  }, []);

  useEffect(() => {
    requestJson<string[]>("/api/informes/ventas/planilla-caja/fuentes")
      .then((lista) => {
        setFuentes(lista);
        if (lista.length > 0) setFuente(lista[0]);
      })
      .catch(() => setFuentes([]));
  }, []);

  // Mapa de controles para lookup rápido por id
  const controlesMap = useMemo(() => {
    const m = new Map<number, ControlCaja>();
    for (const c of controles) m.set(c.idControlCaja, c);
    return m;
  }, [controles]);

  const limpiarFiltros = useCallback(() => {
    setFiltroControl("");
    setFiltroPersona("");
    setFiltroTipoMov("");
    setFiltroTipoRecibo("");
    setFiltroCajero("");
    setFiltroCanal("");
    setFiltroSucursal("");
  }, []);

  // ── Búsqueda: trae controles + todas las planillas en paralelo ─────────────

  const buscar = useCallback(async () => {
    if (!fuente) return;
    setLoading(true);
    setError(null);
    setControles([]);
    setTodosRegistros([]);
    limpiarFiltros();
    setActiveTab("planilla");

    try {
      // 1. Obtener controles del rango
      const params = new URLSearchParams({ fuente, fechaDesde, fechaHasta });
      const ctls = await requestJson<ControlCaja[]>(
        `/api/informes/ventas/planilla-caja/controles?${params}`,
      );
      setControles(ctls);

      if (ctls.length === 0) {
        setFuenteCargada(fuente);
        setRangoLabel(`${fechaDesde} · ${fechaHasta}`);
        setTodosRegistros([]);
        return;
      }

      // 2. Traer todas las planillas en paralelo
      const resultados = await Promise.all(
        ctls.map((c) =>
          requestJson<PlanillaResponse>(
            `/api/informes/ventas/planilla-caja?idControl=${c.idControlCaja}&fuente=${encodeURIComponent(fuente)}`,
          )
            .then((res) => ({ ok: true as const, res, idControl: c.idControlCaja }))
            .catch(() => ({ ok: false as const, idControl: c.idControlCaja })),
        ),
      );

      // 3. Mergear registros, taggear con idControlCaja
      const merged: PlanillaRegistro[] = [];
      for (const r of resultados) {
        if (!r.ok) continue;
        for (const reg of r.res.registros) {
          merged.push({ ...reg, idControlCaja: r.idControl });
        }
      }

      setTodosRegistros(merged);
      setFuenteCargada(fuente);
      setRangoLabel(`${fmtSoloFecha(fechaDesde)} — ${fmtSoloFecha(fechaHasta)}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Error al cargar los datos.");
    } finally {
      setLoading(false);
    }
  }, [fuente, fechaDesde, fechaHasta, limpiarFiltros]);

  // ── Opciones dinámicas de filtros ─────────────────────────────────────────

  const opcionesPersona = useMemo(
    () => unique(todosRegistros.map((r) => r.persona)),
    [todosRegistros],
  );
  const opcionesTipoMov = useMemo(
    () => unique(todosRegistros.map((r) => r.tipoMovimiento ?? r.tipoRecibo)),
    [todosRegistros],
  );
  const opcionesTipoRecibo = useMemo(
    () => unique(todosRegistros.map((r) => r.tipoRecibo)),
    [todosRegistros],
  );
  const opcionesCajero = useMemo(
    () => unique(controles.map((c) => c.cajero)),
    [controles],
  );
  const opcionesCanal = useMemo(
    () => unique(todosRegistros.map((r) => r.canalVenta)),
    [todosRegistros],
  );
  const opcionesSucursal = useMemo(
    () => unique(todosRegistros.map((r) => r.sucursal)),
    [todosRegistros],
  );

  // ── Registros filtrados (base de todos los tabs) ───────────────────────────

  const registrosFiltrados = useMemo(() => {
    return todosRegistros.filter((r) => {
      if (filtroControl && String(r.idControlCaja ?? "") !== filtroControl) return false;
      if (filtroPersona && (r.persona ?? "") !== filtroPersona) return false;
      if (filtroTipoMov && (r.tipoMovimiento ?? r.tipoRecibo ?? "") !== filtroTipoMov) return false;
      if (filtroTipoRecibo && (r.tipoRecibo ?? "") !== filtroTipoRecibo) return false;
      if (filtroCajero) {
        const ctrl = r.idControlCaja != null ? controlesMap.get(r.idControlCaja) : null;
        if ((ctrl?.cajero ?? "") !== filtroCajero) return false;
      }
      if (filtroCanal && (r.canalVenta ?? "") !== filtroCanal) return false;
      if (filtroSucursal && (r.sucursal ?? "") !== filtroSucursal) return false;
      return true;
    });
  }, [
    todosRegistros, filtroControl, filtroPersona, filtroTipoMov,
    filtroTipoRecibo, filtroCajero, filtroCanal, filtroSucursal, controlesMap,
  ]);

  // ── Agregaciones por tab ──────────────────────────────────────────────────

  const resumenSucursal = useMemo(
    () => agrupar(registrosFiltrados, (r) => r.sucursal ?? ""),
    [registrosFiltrados],
  );
  const resumenCanal = useMemo(
    () => agrupar(registrosFiltrados, (r) => r.canalVenta ?? ""),
    [registrosFiltrados],
  );
  const resumenHora = useMemo(() => {
    const rows = agrupar(registrosFiltrados, (r) => {
      const f = r.fechaCarga ?? r.fechaFactura ?? r.fechaRecibo;
      return f ? new Date(f).getHours().toString().padStart(2, "0") + ":00" : "";
    });
    return rows.sort((a, b) => a.grupo.localeCompare(b.grupo));
  }, [registrosFiltrados]);
  const resumenSeccion = useMemo(
    () => agrupar(registrosFiltrados, (r) => seccionLabel[r.seccion] ?? r.seccion ?? ""),
    [registrosFiltrados],
  );
  const resumenClientes = useMemo(
    () => agrupar(registrosFiltrados, (r) => r.persona ?? ""),
    [registrosFiltrados],
  );
  const registrosAnulados = useMemo(
    () => registrosFiltrados.filter((r) => r.estadoCheck === "w"),
    [registrosFiltrados],
  );

  // ── UI helpers ────────────────────────────────────────────────────────────

  const hayFiltros = !!(
    filtroControl || filtroPersona || filtroTipoMov || filtroTipoRecibo ||
    filtroCajero || filtroCanal || filtroSucursal
  );

  const hayDatos = todosRegistros.length > 0;

  // ── Excel export por tab activo ───────────────────────────────────────────

  const exportarExcel = useCallback(() => {
    if (!hayDatos) return;
    const tabLabel = TABS.find((t) => t.id === activeTab)?.label ?? activeTab;
    let filas: Record<string, unknown>[];

    if (activeTab === "planilla" || activeTab === "anulados") {
      const src = activeTab === "anulados" ? registrosAnulados : registrosFiltrados;
      filas = src.map((r) => ({
        Sección: seccionLabel[r.seccion] ?? r.seccion,
        "Nro. Recibo": r.nroRecibo ?? "",
        "Nro. Movimiento": r.nroMovimiento ?? "",
        "Fecha Recibo": fmtFecha(r.fechaRecibo),
        "Fecha Factura": fmtFecha(r.fechaFactura),
        Persona: r.persona ?? "",
        "Tipo Movimiento": r.tipoMovimiento ?? r.tipoRecibo ?? "",
        "Tipo Recibo": r.tipoRecibo ?? "",
        "Canal Venta": r.canalVenta ?? "",
        Sucursal: r.sucursal ?? "",
        "Ingreso Gs.": r.ingresoMonto,
        "Egreso Gs.": r.egresoMonto,
        Estado: r.estadoCheck === "w" ? "Anulado" : r.estadoCheck === "v" ? "OK" : "",
      }));
    } else if (activeTab === "libro") {
      filas = registrosFiltrados.map((r) => {
        const ctrl = r.idControlCaja != null ? controlesMap.get(r.idControlCaja) : null;
        return {
          Fecha: fmtSoloFecha(r.fechaFactura ?? r.fechaRecibo),
          Hora: fmtSoloHora(r.fechaCarga ?? r.fechaFactura ?? r.fechaRecibo),
          "Nro. Factura": r.nroMovimiento ?? r.nroRecibo ?? "",
          "Tipo Movimiento": r.tipoMovimiento ?? "",
          Canal: r.canalVenta ?? "",
          Sucursal: r.sucursal ?? "",
          Fuente: fuenteCargada,
          Cajero: ctrl?.cajero ?? "",
          Caja: ctrl?.caja ?? "",
          "ID Caja": ctrl?.idCaja ?? "",
          "Control #": r.idControlCaja ?? "",
          Estado: r.estadoCheck === "w" ? "Anulado" : r.estadoCheck === "v" ? "OK" : "",
        };
      });
    } else {
      const src: GrupoRow[] =
        activeTab === "sucursal" ? resumenSucursal
        : activeTab === "canal"   ? resumenCanal
        : activeTab === "hora"    ? resumenHora
        : activeTab === "seccion" ? resumenSeccion
        :                           resumenClientes;
      filas = src.map((r) => ({
        Grupo: r.grupo,
        Registros: r.count,
        "Ingreso Gs.": r.ingreso,
        "Egreso Gs.": r.egreso,
        "Neto Gs.": r.ingreso - r.egreso,
      }));
    }

    const ws = XLSX.utils.json_to_sheet(filas);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, tabLabel.slice(0, 31));
    XLSX.writeFile(wb, `planilla_${activeTab}_${fuenteCargada}.xlsx`);
  }, [
    hayDatos, activeTab, fuenteCargada, controlesMap,
    registrosFiltrados, registrosAnulados,
    resumenSucursal, resumenCanal, resumenHora, resumenSeccion, resumenClientes,
  ]);

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="page-content">
      <Container fluid className="px-2 px-md-3">
        <BreadCrumb title="Ventas - Planilla Caja" pageTitle="Informes" />

        {/* ── Panel de filtros ── */}
        <Card className="border-0 shadow-sm mb-3">
          <CardHeader className="bg-transparent border-bottom py-2 d-flex align-items-center gap-2">
            <h6 className="mb-0 me-auto">
              <i className="ri-filter-3-line me-1 text-primary" />
              Filtros
              {hayFiltros && (
                <Badge color="primary" className="ms-2">
                  {registrosFiltrados.length} / {todosRegistros.length}
                </Badge>
              )}
            </h6>
            {hayFiltros && (
              <Button size="sm" color="link" className="py-0 text-danger" onClick={limpiarFiltros}>
                Limpiar filtros
              </Button>
            )}
          </CardHeader>
          <CardBody>
            <Form onSubmit={(e) => { e.preventDefault(); buscar(); }}>
              <div className="d-flex gap-2 align-items-end flex-wrap">

                <div style={{ minWidth: 100 }}>
                  <Label className="form-label form-label-sm">Fuente</Label>
                  <Input type="select" bsSize="sm" value={fuente} onChange={(e) => setFuente(e.target.value)}>
                    {fuentes.length === 0 && <option value="">Cargando...</option>}
                    {fuentes.map((f) => <option key={f} value={f}>{f.toUpperCase()}</option>)}
                  </Input>
                </div>

                <div style={{ minWidth: 120 }}>
                  <Label className="form-label form-label-sm">Desde</Label>
                  <Input type="date" bsSize="sm" value={fechaDesde} onChange={(e) => setFechaDesde(e.target.value)} />
                </div>

                <div style={{ minWidth: 120 }}>
                  <Label className="form-label form-label-sm">Hasta</Label>
                  <Input type="date" bsSize="sm" value={fechaHasta} onChange={(e) => setFechaHasta(e.target.value)} />
                </div>

                <div style={{ minWidth: 180, flex: "1 1 180px" }}>
                  <Label className="form-label form-label-sm">Control de caja</Label>
                  <Input type="select" bsSize="sm" value={filtroControl} onChange={(e) => setFiltroControl(e.target.value)}>
                    <option value="">Todos</option>
                    {controles.map((c) => (
                      <option key={c.idControlCaja} value={String(c.idControlCaja)}>
                        #{c.idControlCaja}{c.caja ? ` · ${c.caja}` : ""}{c.cajero ? ` · ${c.cajero}` : ""}{c.estado === "0" ? " [Abierto]" : " [Cerrado]"}
                      </option>
                    ))}
                  </Input>
                </div>

                <div style={{ minWidth: 160, flex: "2 1 160px" }}>
                  <Label className="form-label form-label-sm">Persona</Label>
                  <Input type="select" bsSize="sm" value={filtroPersona} onChange={(e) => setFiltroPersona(e.target.value)}>
                    <option value="">Todas</option>
                    {opcionesPersona.map((v) => <option key={v} value={v}>{v}</option>)}
                  </Input>
                </div>

                <div style={{ minWidth: 130, flex: "1 1 130px" }}>
                  <Label className="form-label form-label-sm">Tipo movimiento</Label>
                  <Input type="select" bsSize="sm" value={filtroTipoMov} onChange={(e) => setFiltroTipoMov(e.target.value)}>
                    <option value="">Todos</option>
                    {opcionesTipoMov.map((v) => <option key={v} value={v}>{v}</option>)}
                  </Input>
                </div>

                <div style={{ minWidth: 120, flex: "1 1 120px" }}>
                  <Label className="form-label form-label-sm">Tipo recibo</Label>
                  <Input type="select" bsSize="sm" value={filtroTipoRecibo} onChange={(e) => setFiltroTipoRecibo(e.target.value)}>
                    <option value="">Todos</option>
                    {opcionesTipoRecibo.map((v) => <option key={v} value={v}>{v}</option>)}
                  </Input>
                </div>

                <div style={{ minWidth: 120, flex: "1 1 120px" }}>
                  <Label className="form-label form-label-sm">Canal de venta</Label>
                  <Input type="select" bsSize="sm" value={filtroCanal} onChange={(e) => setFiltroCanal(e.target.value)}>
                    <option value="">Todos</option>
                    {opcionesCanal.map((v) => <option key={v} value={v}>{v}</option>)}
                  </Input>
                </div>

                <div style={{ minWidth: 120, flex: "1 1 120px" }}>
                  <Label className="form-label form-label-sm">Sucursal</Label>
                  <Input type="select" bsSize="sm" value={filtroSucursal} onChange={(e) => setFiltroSucursal(e.target.value)}>
                    <option value="">Todas</option>
                    {opcionesSucursal.map((v) => <option key={v} value={v}>{v}</option>)}
                  </Input>
                </div>

                <div style={{ minWidth: 120, flex: "1 1 120px" }}>
                  <Label className="form-label form-label-sm">Cajero</Label>
                  <Input type="select" bsSize="sm" value={filtroCajero} onChange={(e) => setFiltroCajero(e.target.value)}>
                    <option value="">Todos</option>
                    {opcionesCajero.map((v) => <option key={v} value={v}>{v}</option>)}
                  </Input>
                </div>

                <div style={{ minWidth: 90 }}>
                  <Label className="form-label form-label-sm">&nbsp;</Label>
                  <Button type="submit" color="primary" size="sm" className="w-100" disabled={loading || !fuente}>
                    {loading ? <Spinner size="sm" className="me-1" /> : <i className="ri-search-line me-1" />}
                    Buscar
                  </Button>
                </div>

              </div>
            </Form>
          </CardBody>
        </Card>

        {error && (
          <Alert color="danger" className="mb-3">
            <i className="ri-error-warning-line me-1" />{error}
          </Alert>
        )}

        {loading && (
          <div className="text-center py-5">
            <Spinner color="primary" />
            <p className="mt-2 text-muted">Cargando planillas...</p>
          </div>
        )}

        {!loading && hayDatos && controles.length === 0 && (
          <Alert color="info" className="mb-3">
            No se encontraron controles para el rango seleccionado.
          </Alert>
        )}

        {/* ── Planilla con tabs ── */}
        {!loading && hayDatos && (
          <Card className="border-0 shadow-sm">
            {/* Header */}
            <CardHeader className="bg-transparent border-bottom py-2 d-flex justify-content-between align-items-center flex-wrap gap-2">
              <h6 className="mb-0">
                <i className="ri-receipt-line me-1 text-success" />
                {fuenteCargada.toUpperCase()} · {rangoLabel}
                <span className="ms-2 text-muted fw-normal" style={{ fontSize: "0.8rem" }}>
                  {controles.length} controles ·{" "}
                  {registrosFiltrados.length}
                  {hayFiltros && ` de ${todosRegistros.length}`} registros
                </span>
              </h6>
              <Button size="sm" color="success" outline onClick={exportarExcel}>
                <i className="ri-file-excel-line me-1" />
                Excel
              </Button>
            </CardHeader>

            {/* Nav Tabs */}
            <div style={{ overflowX: "auto", borderBottom: "1px solid #e9ebec" }}>
              <Nav
                tabs
                className="flex-nowrap border-0 px-3"
                style={{ minWidth: "max-content", gap: "2px" }}
              >
                {TABS.map((tab) => (
                  <NavItem key={tab.id}>
                    <NavLink
                      href="#"
                      className={classNames(
                        "px-3 py-2 border-0 rounded-0",
                        activeTab === tab.id
                          ? "text-primary fw-semibold border-bottom border-2 border-primary bg-transparent"
                          : "text-muted bg-transparent",
                      )}
                      style={{ fontSize: "0.82rem", whiteSpace: "nowrap", cursor: "pointer" }}
                      onClick={(e) => { e.preventDefault(); setActiveTab(tab.id); }}
                    >
                      <i className={`${tab.icon} me-1`} />
                      {tab.label}
                      {tab.id === "anulados" && registrosAnulados.length > 0 && (
                        <Badge color="danger" pill className="ms-1" style={{ fontSize: "0.6rem" }}>
                          {registrosAnulados.length}
                        </Badge>
                      )}
                    </NavLink>
                  </NavItem>
                ))}
              </Nav>
            </div>

            {/* Tab Content */}
            <CardBody className="p-0">
              <TabContent activeTab={activeTab}>

                <TabPane tabId="planilla">
                  <TablaMovimientos registros={registrosFiltrados} />
                </TabPane>

                <TabPane tabId="sucursal">
                  <SummaryTable rows={resumenSucursal} grupoLabel="Sucursal" />
                </TabPane>

                <TabPane tabId="canal">
                  <SummaryTable rows={resumenCanal} grupoLabel="Canal de Venta" />
                </TabPane>

                <TabPane tabId="hora">
                  <SummaryTable rows={resumenHora} grupoLabel="Hora" />
                </TabPane>

                <TabPane tabId="seccion">
                  <SummaryTable rows={resumenSeccion} grupoLabel="Sección / Condición de Venta" />
                </TabPane>

                <TabPane tabId="anulados">
                  {registrosAnulados.length === 0 ? (
                    <div className="text-center text-muted py-5">
                      <i className="ri-checkbox-circle-line fs-2 text-success" />
                      <p className="mt-2">Sin movimientos anulados.</p>
                    </div>
                  ) : (
                    <TablaMovimientos registros={registrosAnulados} />
                  )}
                </TabPane>

                <TabPane tabId="clientes">
                  <SummaryTable rows={resumenClientes} grupoLabel="Cliente / Persona" />
                </TabPane>

                <TabPane tabId="libro">
                  <div className="table-responsive">
                    <Table className="table-sm table-hover mb-0" striped>
                      <thead className="table-light">
                        <tr>
                          <th>Fecha</th>
                          <th>Hora</th>
                          <th>Nro. Factura</th>
                          <th>Tipo Movimiento</th>
                          <th>Canal</th>
                          <th>Sucursal</th>
                          <th>Fuente</th>
                          <th>Cajero</th>
                          <th>Caja</th>
                          <th>ID Caja</th>
                          <th>Control #</th>
                          <th className="text-center">Estado</th>
                        </tr>
                      </thead>
                      <tbody>
                        {registrosFiltrados.length === 0 ? (
                          <tr>
                            <td colSpan={12} className="text-center text-muted py-4">
                              Sin movimientos para los filtros seleccionados.
                            </td>
                          </tr>
                        ) : (
                          registrosFiltrados.map((r, i) => {
                            const ctrl = r.idControlCaja != null
                              ? controlesMap.get(r.idControlCaja)
                              : null;
                            return (
                              <tr key={i}>
                                <td className="text-nowrap">
                                  {fmtSoloFecha(r.fechaFactura ?? r.fechaRecibo)}
                                </td>
                                <td className="text-nowrap">
                                  {fmtSoloHora(r.fechaCarga ?? r.fechaFactura ?? r.fechaRecibo)}
                                </td>
                                <td className="fw-semibold">
                                  {r.nroMovimiento ?? r.nroRecibo ?? "-"}
                                </td>
                                <td>{r.tipoMovimiento ?? "-"}</td>
                                <td>{r.canalVenta ?? "-"}</td>
                                <td>{r.sucursal ?? "-"}</td>
                                <td>
                                  <Badge color="secondary" style={{ fontSize: "0.65rem" }}>
                                    {fuenteCargada}
                                  </Badge>
                                </td>
                                <td>{ctrl?.cajero ?? "-"}</td>
                                <td>{ctrl?.caja ?? "-"}</td>
                                <td className="text-center">{ctrl?.idCaja ?? "-"}</td>
                                <td className="text-center text-muted" style={{ fontSize: "0.8rem" }}>
                                  #{r.idControlCaja ?? "-"}
                                </td>
                                <td className="text-center">
                                  {r.estadoCheck === "w" ? (
                                    <Badge color="danger" pill>Anulado</Badge>
                                  ) : r.estadoCheck === "v" ? (
                                    <Badge color="success" pill>OK</Badge>
                                  ) : (
                                    <span className="text-muted">-</span>
                                  )}
                                </td>
                              </tr>
                            );
                          })
                        )}
                      </tbody>
                    </Table>
                  </div>
                </TabPane>

              </TabContent>
            </CardBody>
          </Card>
        )}
      </Container>
    </div>
  );
};

export default VentasPlanillaCajaPage;
